import type { PrometheusQuery, ThresholdStep } from '../api/types';

/**
 * Calculate a sensible rate interval based on the time range preset.
 */
function rateInterval(preset: string): string {
  switch (preset) {
    case '1h': return '1m';
    case '6h': return '5m';
    case '24h': return '15m';
    case '7d': return '1h';
    default: return '5m';
  }
}

export interface StatPanelDef {
  title: string;
  unit: string;
  refId: string;
  thresholds: ThresholdStep[];
}

export const SERVICE_OVERVIEW_PANELS: StatPanelDef[] = [
  {
    title: 'Request Rate',
    unit: 'reqps',
    refId: 'reqRate',
    thresholds: [
      { color: 'green', value: 0 },
      { color: 'yellow', value: 0.95 },
      { color: 'red', value: 0.99 },
    ],
  },
  {
    title: 'Error Rate',
    unit: 'percentunit',
    refId: 'errRate',
    thresholds: [
      { color: 'green', value: 0 },
      { color: 'yellow', value: 0.01 },
      { color: 'red', value: 0.05 },
    ],
  },
  {
    title: 'P99 Latency',
    unit: 's',
    refId: 'p99',
    thresholds: [
      { color: 'green', value: 0 },
      { color: 'yellow', value: 0.5 },
      { color: 'red', value: 1 },
    ],
  },
  {
    title: 'P95 Latency',
    unit: 's',
    refId: 'p95',
    thresholds: [
      { color: 'green', value: 0 },
      { color: 'yellow', value: 0.2 },
      { color: 'red', value: 0.5 },
    ],
  },
  {
    title: 'P50 Latency',
    unit: 's',
    refId: 'p50',
    thresholds: [
      { color: 'green', value: 0 },
      { color: 'yellow', value: 0.1 },
      { color: 'red', value: 0.3 },
    ],
  },
  {
    title: 'Active Instances',
    unit: 'short',
    refId: 'instances',
    thresholds: [
      { color: 'red', value: 0 },
      { color: 'yellow', value: 1 },
      { color: 'green', value: 2 },
    ],
  },
];

/**
 * Build the 6 PromQL queries for the Service Overview stat cards.
 */
export function buildServiceOverviewQueries(
  service: string,
  preset: string,
): PrometheusQuery[] {
  const ri = rateInterval(preset);
  const f = `service_name=~"${service}"`;

  return [
    {
      refId: 'reqRate',
      expr: `sum(rate(http_server_request_duration_seconds_count{${f}}[${ri}]))`,
      legendFormat: 'req/s',
    },
    {
      refId: 'errRate',
      expr: `sum(rate(http_server_request_duration_seconds_count{${f}, http_response_status_code=~"5.."}[${ri}])) / sum(rate(http_server_request_duration_seconds_count{${f}}[${ri}]))`,
      legendFormat: 'error %',
    },
    {
      refId: 'p99',
      expr: `histogram_quantile(0.99, sum(rate(http_server_request_duration_seconds_bucket{${f}}[${ri}])) by (le))`,
      legendFormat: 'p99',
    },
    {
      refId: 'p95',
      expr: `histogram_quantile(0.95, sum(rate(http_server_request_duration_seconds_bucket{${f}}[${ri}])) by (le))`,
      legendFormat: 'p95',
    },
    {
      refId: 'p50',
      expr: `histogram_quantile(0.50, sum(rate(http_server_request_duration_seconds_bucket{${f}}[${ri}])) by (le))`,
      legendFormat: 'p50',
    },
    {
      refId: 'instances',
      expr: `count(target_info{${f}})`,
      legendFormat: 'instances',
      instant: true,
    },
  ];
}
